/**
 * Calendar sidebar / detail labels.
 * Prefer API calendar name/summary — never invent titles.
 * V26.1 used payload.sources[].name; events may also carry calendar / calendarName.
 *
 * Note: Google Calendar *list* uses `summary` for the calendar title; event
 * payloads use `summary` for the *event* title. Callers must not pass event
 * title fields into these helpers.
 */

export type CalendarNameFields = {
  name?: string | null;
  /** Calendar list summary only — not an event title. */
  summary?: string | null;
  displayName?: string | null;
  calendarName?: string | null;
  calendar?: string | null;
  accountEmail?: string | null;
  calendarId?: string | null;
};

/** First non-empty string among known calendar name fields. */
export function pickCalendarName(fields: CalendarNameFields): string {
  for (const raw of [
    fields.name,
    fields.summary,
    fields.displayName,
    fields.calendarName,
    fields.calendar,
  ]) {
    const s = typeof raw === "string" ? raw.trim() : "";
    if (s) return s;
  }
  return "";
}

/**
 * Row label for All Calendars.
 * Prefer real calendar name; fall back to account email, then calendar id.
 * When the same name appears under another account, append (email).
 */
export function formatCalendarSourceLabel(
  fields: CalendarNameFields,
  nameAppearsUnderOtherAccount = false,
): string {
  const name = pickCalendarName(fields);
  const email = typeof fields.accountEmail === "string" ? fields.accountEmail.trim() : "";
  if (name) {
    if (nameAppearsUnderOtherAccount && email && name !== email) {
      return `${name} (${email})`;
    }
    return name;
  }
  if (email) return email;
  const id = typeof fields.calendarId === "string" ? fields.calendarId.trim() : "";
  return id || "Calendar";
}

/** Tooltip: Name · email when both differ. */
export function formatCalendarSourceTitle(fields: CalendarNameFields): string {
  const name = pickCalendarName(fields);
  const email = typeof fields.accountEmail === "string" ? fields.accountEmail.trim() : "";
  if (name && email && name !== email) return `${name} · ${email}`;
  return name || email || (typeof fields.calendarId === "string" ? fields.calendarId : "") || "Calendar";
}

/** Event detail kicker: prefer calendar name, then email. */
export function formatEventCalendarKicker(fields: CalendarNameFields): {
  primary: string;
  secondary: string;
} {
  const name = pickCalendarName(fields);
  const email = typeof fields.accountEmail === "string" ? fields.accountEmail.trim() : "";
  if (name && email && name !== email) {
    return { primary: name, secondary: email };
  }
  if (name) return { primary: name, secondary: "" };
  if (email) return { primary: email, secondary: "" };
  const id = typeof fields.calendarId === "string" ? fields.calendarId.trim() : "";
  return { primary: id || "Calendar", secondary: "" };
}
